import {  useEffect, useState } from "react";
import * as Styles from "../core/styles";
import { useTheme } from "../theme/useTheme";
import styled from "styled-components";
import { IconExpandLess, IconExpandMore } from "cdk-radial";
import WorkflowCard from "../core/WorkflowCard";
import {
  BUTTON_SIZES,
  IconButton,
} from "cdk-radial";
import { Workflow } from "./WorkflowInterface";
import { Application } from './ApplicationInterface';
import { IUserIPRestrictionEntity } from "../dealer-admin/useDealerAdminQueries";
import moment from 'moment';
import { useTranslation } from "react-i18next";

export const UserMessageContainer = styled.div`
  margin-top: 32px;
  @media (max-width: 470px) {
    width: 80vw;
    margin-left: 10%;
  }
`;

export const UserMessageDescription = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: ${({ theme }) => theme.carbon.color.element.card.description};
`;

export const UserMessageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px 8px;
  font-weight: 600;
  padding-bottom: 8px;
`;

const StyledWorkflowContainer = styled.div<{
  darkMode: boolean | null;
  isExpanded: boolean;
  height?: string;
}>`
  height: ${({ isExpanded, height }) =>
    isExpanded ? "auto" : height || "52px"};
  border-radius: 4px;
  overflow: ${({ isExpanded }) => (isExpanded ? "auto" : "hidden")};
  padding: 16px;
  margin-bottom: 16px;
  border: 1px solid;
  border-color: ${({ darkMode }) => (darkMode ? "#333" : "#e6e6e6")};
  transition: border-color 1s ease-out;
  @media (max-width: 470px) {
    padding: 16px 5px;
    width: 90vw;
  }
  @media (max-width: 390px) {
    padding: 16px 5px;
  }
`;


const findIpRestricted = (ipRestriction:IUserIPRestrictionEntity[], workflowId:string,appId:string) =>{
  const app = ipRestriction?.find(
    (item:IUserIPRestrictionEntity) => item.entityType === 'application' 
    && item.entityId === appId 
  );
  if(app?.isRestricted){
    return true;
  }
  if(app?.isIpConfigured){
    return false;
  }
  const data = ipRestriction?.some(
    (item:IUserIPRestrictionEntity) => item.entityType === 'workflow' 
    && item.entityId === workflowId 
    && item.isRestricted
  );
  return data;

}

const getTooltipMsg = (ipRestriction:IUserIPRestrictionEntity[], workflowId:string) =>{
  const data = ipRestriction?.find(
    (item:IUserIPRestrictionEntity) => item.entityType === 'workflow' 
    && item.entityId === workflowId 
    && item.isRestricted
  );
  return data;
}

export const getNewIndicator = (app: any ) => {
  if(app.newStatusStartDate && app.newStatusEndDate) {
    const sd = moment.utc(app.newStatusStartDate, 'dddd, MMMM DD, YYYY at h:mm:ss A Z');
    const ed = moment.utc(app.newStatusEndDate, 'dddd, MMMM DD, YYYY at h:mm:ss A Z');
    const now = moment.utc();
    if(now > sd && now < ed) {
      return true;
    }
  }
  return false;
}

function DomainGrouping({
  domain,
  isExpanded,
  workflows,
  onClick,
  onOpenInNewTabClick,
  onCdkUniversityClick,
  onFavoriteChange,
  restrictions,
  onSetDefaultWorkflow,
  isWorkflowDefault
}: {
  domain?: string;
  isExpanded: boolean;
  workflows: Workflow[];
  onClick: Function;
  onOpenInNewTabClick: Function;
  onCdkUniversityClick: Function;
  onFavoriteChange: Function;
  restrictions: IUserIPRestrictionEntity[];
  onSetDefaultWorkflow: (workflowId: string, workflowName: string, isDefault: boolean) => void;
  isWorkflowDefault: (workflowId: string) => boolean;
}) {

  return (
    <div>
      <Styles.DomainHeader>{!!domain && domain!=='undefined' && domain}</Styles.DomainHeader>
      <Styles.CardList>
        {workflows.map((workflow: Workflow, index: number) => (
          <WorkflowCard
            id={workflow.id}
            appId={workflow.appId}
            key={index}
            name={workflow.name}
            description={workflow.description}
            url={workflow.url}
            isFavorite={workflow.isFavorite}
            isEnabled={workflow.isEnabled ?? true}
            iconColor={workflow.icon || ""}
            iconText={workflow.iconText || ""}
            cdkuUrl={workflow.cdkuUrl || ""}
            onClick={onClick}
            onOpenInNewTabClick={onOpenInNewTabClick}
            onCdkUniversityClick={onCdkUniversityClick}
            onFavoriteChange={onFavoriteChange}
            isRestricted={findIpRestricted(restrictions,workflow.id,workflow.appId)}
            restrictedTooltipMsg={getTooltipMsg(restrictions,workflow.id)?.message}
            showNewIndicator={getNewIndicator(workflow)}
            openInNewTab={workflow.openInNewTab}
            isDefault={isWorkflowDefault(workflow.id)}
            onSetDefaultWorkflow={onSetDefaultWorkflow}
            warningMessage={workflow.warningMessage}
            requiredOS={workflow.requiredOS}
          />
        ))}
      </Styles.CardList>
    </div>
  );
}


function Container({
  children,
  isExpanded,
  darkMode,
  closedHeight,
}: {
  children: React.ReactNode;
  isExpanded: boolean;
  darkMode: boolean | null;
  closedHeight?: string;
}){
  return (
    <StyledWorkflowContainer
      data-testid="workflow-container"
      darkMode={darkMode}
      isExpanded={isExpanded}
      height={closedHeight}
    >
      {children}
    </StyledWorkflowContainer>
  );
}

const transformData = (data: any) => {
  return data
    .filter((app: any) => app.workflows && app.workflows.length > 0)
    .map((app: any) => {
      const domainMap = {} as any;
      app.workflows
      .sort((a:any, b:any) => a.domain?.localeCompare(b.domain))
      .forEach((workflow: any) => {
        if (!domainMap[workflow.domain]) {
          domainMap[workflow.domain] = [];
        }
        workflow.id = workflow.workflowId;
        workflow.logoutUrl = app.logoutUrl;
        domainMap[workflow.domain].push(workflow);
      });
      
      const domains = Object.entries(domainMap).map(([name, workflows]) => ({
        name,
        workflows : (workflows as { name: string }[]).sort((a:any, b:any) => a.name.localeCompare(b.name)),
      }));
       
      return { ...app, isExpanded: true, domains };
    });
};

export default function WorkflowList(props: {
    applications: Application[],
    onClick: Function,
    onCdkUniversityClick: Function,
    onOpenInNewTabClick: Function,
    onFavoriteChange: Function,
    restrictions: IUserIPRestrictionEntity[]
    onSetDefaultWorkflow: (workflowId: string, workflowName: string, isDefault: boolean) => void;
    isWorkflowDefault: (workflowId: string) => boolean;
}) {
  const { applications } = props;
  const [apps, setApps] = useState() as any;
  const { darkMode } = useTheme();
  const { t } = useTranslation();
  
  useEffect(()=>{
    setApps(transformData(applications));
  },[applications]);

  const toggleExpand = (id: any) => {
    setApps((apps: any) =>
      apps.map((item: any) =>
        item.id === id ? { ...item, isExpanded: !item.isExpanded } : item
      )
    );
  };

  return (
    <div>

      {apps?.map((app: any, index: number) => (
        <Container isExpanded={app.isExpanded} key={index} darkMode={darkMode}>
          <Styles.AppContainerHeader>
            <Styles.ProductIcon iconColor={app.icon}>
              {app.iconText}
            </Styles.ProductIcon>
            {app.name}
            <IconButton
              icon={app.isExpanded ? <IconExpandLess /> : <IconExpandMore />}
              size={BUTTON_SIZES.SMALL}
              style={{ marginLeft: "auto" }}
              onClick={() => toggleExpand(app.id)}
              text=""
            />
          </Styles.AppContainerHeader>
          {app.domains?.map((domain: any, i: number) => (
            <DomainGrouping
              key={i}
              domain={domain.name}
              isExpanded={app.isExpanded}
              workflows={domain.workflows}
              onClick={props.onClick}
              onOpenInNewTabClick={props.onOpenInNewTabClick}
              onCdkUniversityClick={props.onCdkUniversityClick}
              onFavoriteChange={props.onFavoriteChange}
              restrictions={props.restrictions}
              onSetDefaultWorkflow={props.onSetDefaultWorkflow}
              isWorkflowDefault={props.isWorkflowDefault}
            />
          ))}
        </Container>
      ))}

      {apps?.length === 0 && (
        <Styles.UserMessageContainer>
        <Styles.UserMessageHeader>
          {t('workflowTabs.noWfAssigned')}
        </Styles.UserMessageHeader>
        <Styles.UserMessageDescription>
          {t('workflowTabs.noWfAssignedDesc')}
        </Styles.UserMessageDescription>
      </Styles.UserMessageContainer>
      )
      }

    </div>
  );
}
